// Copyright (c) StrangeBee 2023

import React, { ReactElement } from 'react';
import { useLocalMessage } from '@Helpers/';
import { TaskLogOutput, TaskLogInput, TaskOutput, TaskStatus } from 'thehive-sdk';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import useValidateForm from '@Hooks/useValidateForm';
import { TaskLogForm } from '@Organisms/TaskLog/TaskLogManage/TaskLogForm';

interface TaskLogManageProps {
    triggerElem?: ReactElement;
    onFormFinish: (data: TaskLogInput) => Promise<TaskLogOutput | []>;
    onTaskClose: () => void;
    taskLog?: TaskLogOutput;
    task?: TaskOutput;
    visible?: boolean;
    onVisibleChange?: (visible: boolean) => void;
}

export const TaskLogManage = ({
    triggerElem,
    onFormFinish,
    taskLog,
    onTaskClose,
    task,
    visible,
    onVisibleChange,
}: TaskLogManageProps): ReactElement => {
    const i = useLocalMessage();

    const { form, isValid, onFormValuesChange, fieldsTouched, setFieldsTouched } = useValidateForm();

    const tradLabelKey = taskLog ? 'edit' : 'create';

    return (
        <THDrawer
            title={i(`task-log.${tradLabelKey}.title`)}
            triggerElem={triggerElem}
            className="drawer-form"
            afterVisibleChange={(visible) => {
                form.resetFields();
                onVisibleChange?.(visible);
            }}
            disableClose={fieldsTouched}
            visible={visible}
        >
            {(toggleDrawer) => (
                <TaskLogForm
                    onCancel={() => {
                        toggleDrawer();
                        setFieldsTouched(false);
                        form.resetFields();
                    }}
                    onFinish={({ another, ...values }) => {
                        return onFormFinish(values).then(() => {
                            if (another) {
                                onTaskClose();
                            }

                            toggleDrawer();
                        });
                    }}
                    form={form}
                    isTaskClosed={task?.status === TaskStatus.COMPLETED}
                    isValid={isValid}
                    defaultMessage={taskLog?.message}
                    defaultAttachments={taskLog?.attachments || []}
                    defaultIncludeInTimeline={taskLog?.includeInTimeline || 0}
                    onChange={() => {
                        onFormValuesChange();
                        setFieldsTouched(true);
                    }}
                />
            )}
        </THDrawer>
    );
};
